import React, { useEffect } from 'react';
import { Cookie } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const CookiePolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentDate = new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
              <Cookie className="w-7 h-7 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-800">Cookie Policy</h1>
              <p className="text-slate-500">G&B Wood</p>
            </div>
          </div>
          <p className="text-lg text-slate-600 leading-relaxed">
            Il presente sito utilizza cookie tecnici e, previo consenso, cookie di analisi statistica.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            
            {/* 1. Cosa sono i cookie */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">1. Cosa sono i cookie</h2>
              <p className="text-slate-600 leading-relaxed">
                I cookie sono piccoli file di testo che i siti visitati inviano al browser dell'utente, dove vengono memorizzati per essere poi ritrasmessi agli stessi siti alla visita successiva. Consentono il corretto funzionamento del sito e un'esperienza di navigazione più efficiente.
              </p>
            </section>

            {/* 2. Tipologie di cookie utilizzati */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">2. Tipologie di cookie utilizzati</h2>
              
              {/* a) Cookie tecnici */}
              <div className="bg-slate-50 rounded-xl p-5 mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">a) Cookie tecnici (obbligatori)</h3>
                <ul className="list-disc list-inside text-slate-600 mb-4 space-y-1">
                  <li>necessari al funzionamento del sito</li>
                  <li>permettono la navigazione e l'utilizzo delle funzionalità (moduli, sessioni)</li>
                  <li>non richiedono il consenso dell'utente</li>
                </ul>
                <p className="text-slate-600 text-sm">
                  <strong>Esempi:</strong> cookie di sessione, cookie per il salvataggio delle preferenze.
                </p>
              </div>

              {/* b) Cookie di analisi */}
              <div className="bg-slate-50 rounded-xl p-5 mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">b) Cookie di analisi / statistici (facoltativi)</h3>
                <p className="text-slate-600 leading-relaxed mb-3">
                  Utilizzati per raccogliere informazioni in forma aggregata e anonima sul numero di utenti e su come visitano il sito (pagine più viste, tempo di permanenza, ecc.).
                </p>
                <p className="text-slate-600 leading-relaxed mb-3">
                  Vengono installati solo previo consenso tramite il banner iniziale.
                </p>
                <p className="text-slate-600 text-sm">
                  <strong>Esempio:</strong> Google Analytics con IP anonimizzato (se attivato).
                </p>
              </div>

              {/* c) Cookie di terze parti */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">c) Cookie di terze parti</h3>
                <p className="text-slate-600 leading-relaxed mb-3">
                  Eventuali servizi integrati (es. mappe Google, video embedded, pulsanti social) possono installare cookie di terze parti.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  La gestione di questi cookie è regolata dalle informative dei rispettivi fornitori.
                </p>
              </div>
            </section>

            {/* 3. Gestione del consenso */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">3. Gestione del consenso</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Alla prima visita viene mostrato un banner cookie che permette all'utente di:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 space-y-1">
                <li>accettare tutti i cookie</li>
                <li>rifiutare i cookie non tecnici</li>
                <li>personalizzare le preferenze per categoria</li>
              </ul>
              <p className="text-slate-600 leading-relaxed">
                L'utente può modificare in qualsiasi momento le proprie scelte attraverso l'apposita sezione "Impostazioni Cookie" (link nel footer).
              </p>
            </section>

            {/* 4. Durata dei cookie */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">4. Durata dei cookie</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Cookie di sessione:</strong> eliminati alla chiusura del browser</li>
                <li><strong>Cookie persistenti:</strong> durata variabile fino a 12 mesi (salvo cancellazione manuale da parte dell'utente)</li>
              </ul>
            </section>

            {/* 5. Come disabilitare i cookie dal browser */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">5. Come disabilitare i cookie dal browser</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                L'utente può configurare il proprio browser per rifiutare i cookie o cancellarli. Alcune funzionalità del sito potrebbero però non essere disponibili.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Le istruzioni aggiornate sono disponibili nelle sezioni di assistenza dei browser principali (Chrome, Firefox, Safari, Edge, ecc.).
              </p>
            </section>

            {/* 6. Aggiornamenti */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">6. Aggiornamenti</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                La presente Cookie Policy può essere soggetta ad aggiornamenti.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Ultimo aggiornamento:</strong> {currentDate}
              </p>
            </section>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
